// Login — usa Maestro Design System (classes m-*). Mantém a integração atual
// com useAuth(authenticate) e exibe erro de rede via toast.
//
// Padrão Maestro aplicado:
// - canto reto em tudo (sem border-radius)
// - Canela para o título, Satoshi (fallback Helvetica) para o resto
// - comando em caixa de frase, verbo+objeto ("Entrar")
// - erro curto, sem desculpa

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useAuth } from '../../shared/Auth/UseAuth';
import { Button } from '../../shared/components/Button';
import { TextField } from '../../shared/components/TextField';
import { BrandMark } from '../../shared/components/BrandMark';
import { useToast } from '../../shared/components/Toast';

const schema = z.object({
  userName: z
    .string()
    .min(1, 'Informe seu e-mail ou usuário')
    .max(200, 'Longo demais'),
  password: z.string().min(1, 'Informe sua senha'),
});

type FormValues = z.infer<typeof schema>;

export default function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [authError, setAuthError] = useState<string | undefined>();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { userName: '', password: '' },
  });

  async function onSubmit(values: FormValues) {
    setAuthError(undefined);
    try {
      await auth.authenticate(
        values.userName,
        values.password,
        '',
        (msg: string) => setAuthError(msg),
        navigate,
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Falha ao autenticar';
      setAuthError(msg);
      toast.error('Login falhou', msg);
    }
  }

  return (
    <section
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-6)',
        background: 'var(--paper)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 920,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          background: 'var(--surface)',
          border: 'var(--border-hairline) solid var(--line)',
          minHeight: 480,
        }}
      >
        <div
          style={{
            padding: 'var(--space-7)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-5)',
          }}
        >
          <BrandMark />

          <header style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <h1 className="t-title" style={{ margin: 0 }}>
              Bom te ver.
            </h1>
            <p className="t-body" style={{ margin: 0, color: 'var(--ink-muted)' }}>
              Entre com seu e-mail de trabalho.
            </p>
          </header>

          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
          >
            <TextField
              label="E-mail ou usuário"
              autoComplete="username"
              autoFocus
              error={errors.userName?.message}
              {...register('userName')}
            />
            <TextField
              label="Senha"
              type="password"
              autoComplete="current-password"
              error={errors.password?.message}
              {...register('password')}
            />

            {authError && (
              <div
                role="alert"
                className="m-banner"
                style={{
                  padding: 'var(--space-3)',
                  background: 'var(--danger-soft)',
                  color: 'var(--ink)',
                  borderLeft: 'var(--border-rule) solid var(--danger)',
                }}
              >
                {authError}
              </div>
            )}

            <Button type="submit" loading={isSubmitting}>
              {isSubmitting ? 'Entrando…' : 'Entrar'}
            </Button>

            <Button
              type="button"
              variant="quiet"
              onClick={() => navigate('/forgot-password')}
            >
              Esqueci minha senha
            </Button>
          </form>
        </div>

        <aside
          aria-hidden="true"
          style={{
            background: 'var(--backstage)',
            color: 'var(--on-backstage)',
            padding: 'var(--space-7)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            position: 'relative',
            overflow: 'hidden',
            borderLeft: `6px solid var(--backstage-marker)`,
          }}
        >
          <span
            className="t-label"
            style={{ color: 'var(--backstage-marker)', opacity: 0.9 }}
          >
            Brand Universe
          </span>
          <blockquote className="t-quote" style={{ margin: 0, maxWidth: '28ch' }}>
            Cada marca é um mundo. Entre pelo seu.
          </blockquote>
          <span className="t-meta" style={{ opacity: 0.7 }}>
            — Enredo · {new Date().getFullYear()}
          </span>
        </aside>
      </div>
    </section>
  );
}
